<template>
  <div
    class="vcore_input-group"
    :class="{ 'vcore_has-error': !!error, 'feedback-padding': !error }"
  >
    <label
      v-if="label"
      class="vcore_input-label"
      :class="{ 'vcore_input-label_optional': !required && !disableOptionalTag}"
      :style="{ color: labelColor }"
      :for="id | idFormat"
      :id="ariaLabelledBy | ariaFormat"
      >
      {{ label }}
    </label>
    <button v-if="popover" :ref="`${id}_label-icon` | idFormat" class="popover-trigger"
      aria-label="More info" type="button">
      <i class="vcore_icon icon-info"></i>
    </button>
    <Popover v-if="popover && popoverTarget"
             :placement="popover.placement"
             :target="popoverTarget"
             :title="popover.title"
             :content="popover.content"
             :id="popover.id"
    ></Popover>
    <masked-input
      v-if="maskOptions"
      v-model="content"
      v-on="maskEvents"
      @blur="handleBlur"
      @focusin="handleFocusIn"
      @focusout="handleFocusOut"
      :type="type"
      :class="{ 'vcore_input--error': !!error, [size]: !!size }"
      class="vcore_input"
      :placeholder="placeholder"
      v-bind="maskOptions"
      :name="name || label"
      :aria-labelledby="ariaLabelledBy | ariaFormat"
      :aria-invalid="`${!!error}`"
      :aria-required="`${required}`"
      :aria-errormessage="ariaErrorMessage"
      :id="id | idFormat"
      :disabled="disabled"
      :autocomplete="autocomplete"
      :inputmode="inputmode"
      :pattern="pattern"
    >
    </masked-input>
    <input
      v-else
      :type="type"
      :value="content"
      v-on="plainEvents"
      @blur="handleBlur"
      @focusin="handleFocusIn"
      @focusout="handleFocusOut"
      class="vcore_input"
      :placeholder="placeholder"
      :class="{ 'vcore_input--error': !!error, [size]: !!size }"
      :name="name || label"
      :aria-labelledby="ariaLabelledBy | ariaFormat"
      :aria-invalid="`${!!error}`"
      :aria-required="`${required}`"
      :aria-errormessage="ariaErrorMessage"
      :id="id | idFormat"
      :disabled="disabled"
      :autocomplete="autocomplete"
      :guidance="guidance"
      :inputmode="inputmode"
      :pattern="pattern"
    />
    <span class="vcore_input-addon"> </span>
    <div
      class="vcore_input-feedback feedback-position"
      :id="errorId | idFormat"
      v-if="error"
      aria-live="assertive"
    >{{ error }}</div>
    <div
      class="vcore_input-feedback feedback-position"
      :style="hint.style || {}"
      :id="hintId | idFormat"
      v-else-if="hint && !!hintText"
      aria-live="assertive"
    >
      <i class="vcore_icon" :class="hint.icon" :style="hint.iconStyle || {}"/>
      {{ hintText }}
    </div>
    <div>{{guidanceText}}</div>
  </div>
</template>

<script>
import MaskedInput from "vue-text-mask";
import Popover from "./Popover";
import { resolvePropertyByString } from "../utils/Object";

export default {
  name: "Input",
  props: {
    type: {
      default: "text"
    },
    label: String,
    name: String,
    value: {},
    placeholder: String,
    labelStyle: String,
    inputStyle: String,
    size: {
      default: ""
    },
    labelColor: String,
    maskOptions: Object,
    required: Boolean,
    error: String,
    hint: Object,
    formName: String,
    popover: Object,
    trim: Boolean,
    validStatus: Boolean,
    guidanceText: String,
    disabled: {
      type: Boolean,
      default: false
    },
    triggers: {
      type: Object,
      default: () => ({})
    },
    validateOn: {
      default: "input"
    },
    disableOptionalTag: {
      type: Boolean,
      default: false
    },
    inputmode: String,
    pattern: String,
    autocomplete: {
      type: String,
      default: "off"
    }
  },
  components: {
    MaskedInput,
    Popover
  },
  data() {
    return {
      content: this.value,
      popoverTarget: null
    };
  },
  watch: {
    value() {
      this.content = this.value;
    },
    validStatus(val) {
      if (val) {
        if (!!this.triggers.valid) {
          this.$store.dispatch(this.triggers.valid, this.value);
        }
      } else {
        if (!!this.triggers.invalid) {
          this.$store.dispatch(this.triggers.invalid);
        }
      }
    }
  },
  computed: {
    id() {
      if (this.formName) {
        return `${this.formName}_${this.label}`;
      }
      return this.label || this.name;
    },

    ariaLabelledBy() {
      return `${this.id}--label`;
    },

    ariaErrorMessage() {
      return this.error ? this.$options.filters.idFormat(this.errorId) : undefined;
    },

    errorId() {
      return `${this.id}--error`;
    },

    hintId() {
      return `${this.id}--hint`;
    },

    hintText() {
      if (this.hint.type === "store") {
        return resolvePropertyByString(this.$store.state, this.hint.value);
      } else {
        return this.hint.value;
      }
    },

    maskEvents() {
      return { [this.validateOn]: this.handleMaskInput };
    },

    plainEvents() {
      return { [this.validateOn]: this.handlePlainInput };
    }
  },
  methods: {
    handleMaskInput(event) {
      this.$emit("input", this.content);
    },

    handlePlainInput(event) {
      this.$emit("input", event.target.value);
    },
    handleBlur() {
      if (this.trim) {
        this.$emit("input", this.content.trim());
      }
      this.$emit("blur");
    },
    handleFocusIn() {
      this.$emit("focusin");
    },
    handleFocusOut() {
      this.$emit("focusout");
    },
  },
  mounted() {
    this.popoverTarget = this.$refs[`${this.$options.filters.idFormat(this.id)}_label-icon`];
  }
};
</script>

<style lang="scss" scoped>
input.vcore_input {
  &.small {
    height: 30px;
  }

  &.large {
    height: 50px;
  }
}

.vcore_input-group {

  .vcore_input-label + button.popover-trigger {
    display: inline-block;
    color: $color_dark-blue;
    cursor: pointer;
    margin-left: 4px;

    .vcore_icon {
      font-size: 16px;
    }
  }
}

.feedback-position {
  position: absolute;
}

select {
  cursor: pointer;
}

.popover-trigger {
  border-radius: 500px;
  border: none;
  background: none;
  width: fit-content;
  height: fit-content;
  padding: 0;
  margin-left: 4px;
}

.popover-trigger:focus-visible {
  outline: 2px solid black;
  outline-offset: 2px;
}
</style>
